import { Injectable, Injector } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Router } from '@angular/router';
import { SessionStateService } from '../../core/session-state.service';

@Injectable()
export class AuthInterceptorService implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject: BehaviorSubject<any> = new BehaviorSubject<any>(
    null,
  );

  constructor(
    private injector: Injector,
    private router: Router,
    private sessionState: SessionStateService,
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const token = this.sessionState.token();
    const language =
      (typeof localStorage !== 'undefined'
        ? localStorage.getItem('language')
        : 'en') || 'en';

    let authReq = req;
    if (token) {
      authReq = this.addTokenHeader(req, token);
    }

    // Always add language
    authReq = authReq.clone({
      headers: authReq.headers.set('Accept-Language', language),
    });

    return next.handle(authReq).pipe(
      catchError((error) => {
        if (error instanceof HttpErrorResponse && error.status === 401) {
          // Ignore 401 for login endpoint to allow the component to handle specific error messages
          // specifically for the "Unregistered User" case
          if (req.url.includes('/api/Authentication/login')) {
            return throwError(() => error);
          }
          return this.handle401Error(authReq, next);
        }
        if (error instanceof HttpErrorResponse && error.status === 403) {
          return this.handle403Error(authReq, next);
        }
        return throwError(() => error);
      }),
    );
  }

  private handle403Error(request: HttpRequest<any>, next: HttpHandler) {
    const authService = this.injector.get(AuthService);
    // On 403, try to refresh user profile to see if roles changed
    return authService.refreshUserProfile().pipe(
      switchMap(() => {
        // After refresh, the sessionState signal will be updated.
        // We can't really "fix" the request if the role really changed to something with less permissions,
        // so we just let the error propagate or redirect if needed.
        // For now, just re-throw after syncing state so UI can react.
        return throwError(() => new Error('Forbidden: Syncing profile...'));
      }),
      catchError((err) => {
        return throwError(() => err);
      }),
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const authService = this.injector.get(AuthService);

      return authService.refreshToken().pipe(
        switchMap((response: any) => {
          this.isRefreshing = false;
          const newToken = this.sessionState.token();
          this.refreshTokenSubject.next(newToken);

          return next.handle(this.addTokenHeader(request, newToken!));
        }),
        catchError((err) => {
          this.isRefreshing = false;

          // Clear session and redirect to login
          authService.logout();
          this.router.navigate(['/login']);
          return throwError(() => err);
        }),
      );
    }

    return this.refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next.handle(this.addTokenHeader(request, token));
      }),
    );
  }

  private addTokenHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      headers: request.headers.set('Authorization', `Bearer ${token}`),
    });
  }
}
